import { forwardRef, useImperativeHandle, useState } from "react";
import { MiMenu } from "@miplatform/components";
import { PERMISSIONS } from "@miplatform/constants";
import {
  mdiArrowULeftTop,
  mdiCalendarStartOutline,
  mdiCancel,
  mdiCheck,
  mdiClockOutline,
  mdiFormatListBulleted,
  mdiHandBackRightOutline,
} from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { useOrdersContext } from "../../OrdersProvider";
import { CancelOrderModal } from "./CancelOrderModal";
import { ForceCompleteOrderModal } from "./ForceCompleteOrderModal";
import { HoldOrderModal } from "./HoldOrderModal";
import { Push24hOrderModal } from "./Push24hOrderModal";
import { RescheduleOrderModal } from "./RescheduleOrderModal";
import { UndoApproveModal } from "./UndoApproveModal";
import { UndoAssignmentModal } from "./UndoAssignmentModal";
import { UndoConfirmationModal } from "./UndoConfirmationModal";
import { useOrdersListActionMenuViewModel } from "./useOrdersListActionMenuViewModel";

export const OrdersListActionMenu = forwardRef((props, ref) => {
  const {
    order,
    orderReasons,
    id: orderId,
    modifiedOn,
    onRefresh,
    menuViewConfig = {
      Reschedule: true,
      Hold: true,
      Cancel: true,
      OrderActivityDetails: true,
      ForceComplete: true,
      Push24h: false,
      UndoConfirmation: false,
      UndoApprove: false,
      UndoAssignment: false,
    },
  } = props;

  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isHoldModalOpen, setIsHoldModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isForceCompleteModalOpen, setIsForceCompleteModalOpen] =
    useState(false);
  const [isPush24hModalOpen, setIsPush24hModalOpen] = useState(false);
  const [isUndoConfirmationModalOpen, setIsUndoConfirmationModalOpen] =
    useState(false);
  const [isUndoApproveModalOpen, setIsUndoApproveModalOpen] = useState(false);
  const [isUndoAssignmentModalOpen, setIsUndoAssignmentModalOpen] =
    useState(false);

  const { openDrawer, setSelectedOrder } = useOrdersContext();

  const { hasPermission } = usePermissions();

  const canCancelOrder = hasPermission(PERMISSIONS.ORDER.CANCEL);

  useImperativeHandle(ref, () => ({
    openRescheduleModal: () => setIsRescheduleModalOpen(true),
    openHoldModal: () => setIsHoldModalOpen(true),
    closeModals: () => {
      setIsRescheduleModalOpen(false);
      setIsHoldModalOpen(false);
      setIsCancelModalOpen(false);
      setIsForceCompleteModalOpen(false);
      setIsPush24hModalOpen(false);
      setIsUndoConfirmationModalOpen(false);
      setIsUndoApproveModalOpen(false);
      setIsUndoAssignmentModalOpen(false);
    },
    order,
  }));

  const theme = useTheme();

  const {
    isLoading,
    rescheduleOrder,
    holdOrder,
    cancelOrder,
    forceCompleteOrder,
    push24hOrder,
    undoConfirmation,
    undoApprove,
    undoAssignment,
  } = useOrdersListActionMenuViewModel({ onRefresh });

  const handleReschedule = async (data) => {
    await rescheduleOrder(orderId, {
      ...data,
      modifiedOn,
    });
    setIsRescheduleModalOpen(false);
  };

  const handleHold = async (data) => {
    await holdOrder(orderId, {
      ...data,
      modifiedOn,
    });
    setIsHoldModalOpen(false);
  };

  const handleCancel = async (data) => {
    await cancelOrder(orderId, {
      reason: data.reason,
      modifiedOn,
    });
    setIsCancelModalOpen(false);
  };

  const handleUndoConfirmation = async (data) => {
    await undoConfirmation(orderId, {
      ...data,
      modifiedOn,
    });
    setIsUndoConfirmationModalOpen(false);
  };

  const handleUndoApprove = async (data) => {
    await undoApprove(orderId, {
      ...data,
      modifiedOn,
    });
    setIsUndoApproveModalOpen(false);
  };
  const handleUndoAssignment = async (data) => {
    await undoAssignment(orderId, {
      ...data,
      modifiedOn,
    });
    setIsUndoAssignmentModalOpen(false);
  };

  const handleForceComplete = async () => {
    await forceCompleteOrder({ orderId, modifiedOn });
    setIsForceCompleteModalOpen(false);
  };

  const handlePush24h = async () => {
    await push24hOrder(orderId, modifiedOn);
    setIsPush24hModalOpen(false);
  };

  return (
    <>
      <MiMenu
        data={[
          {
            label: "Change Requested Date",
            icon: mdiCalendarStartOutline,
            color: theme.palette.blue.dark,
            action: () => setIsRescheduleModalOpen(true),
            hide: !menuViewConfig.Reschedule,
          },
          {
            label: "Hold",
            icon: mdiHandBackRightOutline,
            color: theme.palette.purple.dark,
            action: () => setIsHoldModalOpen(true),
            hide: !menuViewConfig.Hold,
          },
          {
            label: "Force Closeout",
            icon: mdiCheck,
            color: theme.palette.success.main,
            action: () => setIsForceCompleteModalOpen(true),
            hide: !menuViewConfig.ForceComplete,
          },
          {
            label: "Push 24h",
            icon: mdiClockOutline,
            color: theme.palette.blue.main,
            action: () => setIsPush24hModalOpen(true),
            hide: !menuViewConfig.Push24h,
          },
          {
            label: "Details",
            icon: mdiFormatListBulleted,
            color: theme.palette.grey.main,
            action: () => {
              setSelectedOrder(order);
              openDrawer();
            },
            hide: !menuViewConfig.OrderActivityDetails,
          },
          {
            label: "Cancel",
            icon: mdiCancel,
            color: theme.palette.red.dark,
            action: () => setIsCancelModalOpen(true),
            hide: !canCancelOrder || !menuViewConfig.Cancel,
          },
          {
            label: "Undo Confirmation",
            icon: mdiArrowULeftTop,
            color: theme.palette.orange.main,
            action: () => setIsUndoConfirmationModalOpen(true),
            hide: !menuViewConfig.UndoConfirmation,
          },
          {
            label: "Undo Approve",
            icon: mdiArrowULeftTop,
            color: theme.palette.orange.main,
            action: () => setIsUndoApproveModalOpen(true),
            hide: !menuViewConfig.UndoApprove,
          },
          {
            label: "Undo Assignment",
            icon: mdiArrowULeftTop,
            color: theme.palette.orange.main,
            action: () => setIsUndoAssignmentModalOpen(true),
            hide: !menuViewConfig.UndoAssignment,
          },
        ]}
      />

      <RescheduleOrderModal
        open={isRescheduleModalOpen}
        onClose={() => setIsRescheduleModalOpen(false)}
        onReschedule={handleReschedule}
        orderReasons={orderReasons}
        isLoading={isLoading}
      />
      <HoldOrderModal
        open={isHoldModalOpen}
        onClose={() => setIsHoldModalOpen(false)}
        onHold={handleHold}
        isLoading={isLoading}
      />
      <CancelOrderModal
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
      <UndoConfirmationModal
        open={isUndoConfirmationModalOpen}
        onClose={() => setIsUndoConfirmationModalOpen(false)}
        onUndo={handleUndoConfirmation}
        isLoading={isLoading}
      />
      <UndoApproveModal
        open={isUndoApproveModalOpen}
        onClose={() => setIsUndoApproveModalOpen(false)}
        onUndo={handleUndoApprove}
        isLoading={isLoading}
      />
      <UndoAssignmentModal
        open={isUndoAssignmentModalOpen}
        onClose={() => setIsUndoAssignmentModalOpen(false)}
        onUndo={handleUndoAssignment}
        isLoading={isLoading}
      />

      <ForceCompleteOrderModal
        open={isForceCompleteModalOpen}
        onClose={() => setIsForceCompleteModalOpen(false)}
        onForceComplete={handleForceComplete}
        isLoading={isLoading}
      />
      <Push24hOrderModal
        open={isPush24hModalOpen}
        onClose={() => setIsPush24hModalOpen(false)}
        onPush={handlePush24h}
        isLoading={isLoading}
      />
    </>
  );
});
